import { useEffect, useState } from "react";

export const useMicrophones = () => {
  const [stream, setStream] = useState<MediaStream | null>(null);
  const [isMuted, setIsMuted] = useState<boolean>(false);
  const [selectedMicrophone, setSelectedMicrophone] =
    useState<MediaDeviceInfo | null>(null);

  const getMicrophones = async () => {
    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      const audioInputs = devices
        .filter(device => device.kind === "audioinput")
        .map(device => ({
          ...device,
          deviceId: device.deviceId,
          label: device.label || `Microphone ${device.deviceId}`,
        }));

      return audioInputs;
    } catch (error) {
      console.error("Error fetching microphones:", error);
    }
  };

  const connectMicrophone = async () => {
    try {
      const devices = await getMicrophones();
      if (stream) {
        stream.getTracks().forEach(track => track.stop());
      }

      const defaultMicrophone = devices?.[0];

      const newStream = await navigator.mediaDevices.getUserMedia({
        audio: {
          noiseSuppression: true,
          echoCancellation: true,
          autoGainControl: true,
        },
      });

      setSelectedMicrophone(defaultMicrophone || null);
      setStream(newStream);
      setIsMuted(false);
      return newStream;
    } catch (error) {
      console.error("Error accessing microphone:", error);
      return null;
    }
  };

  const muteMicrophone = (mute: boolean) => {
    if (stream) {
      stream.getAudioTracks().forEach(track => {
        track.enabled = !mute;
      });
      setIsMuted(mute);
    }
  };

  useEffect(() => {
    navigator.mediaDevices.addEventListener("devicechange", connectMicrophone);

    return () => {
      navigator.mediaDevices.removeEventListener(
        "devicechange",
        connectMicrophone,
      );
    };
  }, [selectedMicrophone]);

  useEffect(() => {
    return () => {
      if (stream) {
        stream.getTracks().forEach(track => track.stop());
      }
    };
  }, [stream]);

  return {
    selectedMicrophone,
    isMuted,
    stream,
    connectMicrophone,
    muteMicrophone,
  };
};
